import { Fragment } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { PlateCalculation } from '@heraion/shared';

import { formatUnitWeight, weightLocale } from './plateCalcMath';
import { weightUnitLabels } from '../weightUnitLabels';
import type { makeStyles } from './plateCalcModalStyles';
import type { WeightUnit } from './state/loggerDraft';

// The plate calculator's ANSWER: what goes on the bar, or why nothing can.
//
// Split out of PlateCalcModal in LG-37b fetta B, and the reason is the slice
// itself: this block now has TWO producers. It used to render whatever the
// greedy resolved from a typed target; it now renders that OR the composition
// the athlete built by tapping plates. Both arrive as a `PlateCalculation`, so
// the summary never learns which direction it came from — that is the whole
// point of composing back into the calculator's own shape.
//
// ⚠️ `mode` is what decides "pairs" vs "plates" in every line here, not the
// athlete's stored preference: a composition and a greedy solution both carry
// their own mode, and reading it off anything else is how the number and the
// word stop agreeing.
//
// ⭐ TAKING A PLATE OFF happens here, and that is Antenore's correction of
// 10/09 («metti il meno nel frame sotto dove si mostrano i pesi»). The compose
// row above adds; each row here already names one plate and says how many of
// it are on the bar, so it is the row that can lose one. The alternative —
// a "−" beside every chip in the row above — turned eight weights into three
// wrapped lines and pushed this card off the screen.
//
// ⛔ The "−" only appears when the caller passes `onRemovePlate`. Bodyweight
// rows do not compose (the load hangs off a belt, so "pairs" describes
// nothing), and this card is shown for them too.

type Props = {
  // ⛔ No `theme`: this block reads every colour and size off `styles`, so
  // taking one would be a prop nobody uses kept for symmetry with its two
  // sibling sections.
  styles: ReturnType<typeof makeStyles>;
  calc: PlateCalculation;
  /** The ROW's unit — every number on this card is already expressed in it, so
   *  this only says how to WRITE them. ⛔ Not the profile preference: the
   *  athlete toggles kg/lb per set, and this card must say what that row says
   *  (LG-37b E1, inbox 66c51036). */
  unit: WeightUnit;
  /** Shown under the card when the loading shape is one-sided — it explains
   *  which lifts load that way, so it belongs to the ANSWER, not the picker. */
  showSingleSideHint: boolean;
  /** Take one plate of this size off the bar. Omit to render the summary
   *  read-only, which is what bodyweight rows get. */
  onRemovePlate?: (kg: number) => void;
};

export function PlateCalcSummary({
  styles,
  calc,
  unit,
  showSingleSideHint,
  onRemovePlate,
}: Props) {
  const { t, i18n } = useTranslation();
  // ⛔ The locale, not `formatWeight`'s "en-US" default: every weight on this
  // card sits on the same screen as the history rows, which have always passed
  // it. Two decimal marks in one view is the whole of the E1 finding.
  const locale = weightLocale(i18n);
  const unitLabels = weightUnitLabels(t);

  return (
    <Fragment>
      <View style={styles.outputCard}>
        {calc.ok ? (
          <>
            <Text style={styles.outputHeader}>
              {calc.mode === 'single'
                ? t('mobile.plates.loadedSide', {
                    weight: formatUnitWeight(calc.perSide, unit, locale, unitLabels),
                  })
                : t('mobile.plates.perSide', {
                    weight: formatUnitWeight(calc.perSide, unit, locale, unitLabels),
                  })}
            </Text>
            {calc.pairsLoaded.length === 0 ? (
              <Text style={styles.outputBody}>
                {t('mobile.plates.barOnly')}
              </Text>
            ) : (
              calc.pairsLoaded.map((p) => (
                <View key={p.size} style={styles.plateRow}>
                  <Text style={styles.plateKg}>
                    {formatUnitWeight(p.size, unit, locale, unitLabels)}
                  </Text>
                  <Text style={styles.platePairs}>
                    {calc.mode === 'single'
                      ? t('mobile.plates.plates', { count: p.pairs })
                      : t('mobile.plates.pairs', { count: p.pairs })}
                  </Text>
                  {onRemovePlate ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={t(
                        calc.mode === 'single'
                          ? 'mobile.plates.composeRemovePlate'
                          : 'mobile.plates.composeRemovePair',
                        { weight: formatUnitWeight(p.size, unit, locale, unitLabels) },
                      )}
                      onPress={() => onRemovePlate(p.size)}
                      style={({ pressed }) => [
                        styles.composeStepButton,
                        pressed && styles.stepButtonPressed,
                      ]}
                    >
                      <Text style={styles.stepButtonText}>−</Text>
                    </Pressable>
                  ) : null}
                </View>
              ))
            )}
            {calc.remainder > 0 ? (
              <Text style={styles.remainder}>
                {t('mobile.plates.remainder', {
                  weight: formatUnitWeight(calc.remainder, unit, locale, unitLabels),
                })}
              </Text>
            ) : null}
          </>
        ) : calc.reason === 'below_bar' ? (
          <Text style={styles.outputBody}>{t('mobile.plates.belowBar')}</Text>
        ) : (
          <Text style={styles.outputBody}>{t('mobile.plates.invalid')}</Text>
        )}
      </View>
      {showSingleSideHint ? (
        <Text style={styles.singleSideHint}>
          {t('mobile.plates.singleSideHint')}
        </Text>
      ) : null}
    </Fragment>
  );
}
